import React, { FC, useState } from 'react'
import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js'
import { useFormStyles } from './form-styles'
import { fontFamily, stripeColor } from './tokens'

// The part of Stripe's element change event we use.
interface CardFieldChange {
  complete: boolean
  error?: { message?: string }
}

export type CardPart = 'number' | 'expiry' | 'cvc'

export interface CardFormValue {
  name: string
  complete: Record<CardPart, boolean>
}

export const EMPTY_CARD: CardFormValue = {
  name: '',
  complete: { number: false, expiry: false, cvc: false }
}

export const isCardReady = ({ name, complete }: CardFormValue): boolean =>
  name.trim().length > 1 && complete.number && complete.expiry && complete.cvc

interface CellularCardFormProps {
  value: CardFormValue
  disabled?: boolean
  // Takes an updater, since Stripe fields can report changes in the same tick.
  onChange: (update: (previous: CardFormValue) => CardFormValue) => void
}

// Matches the mock's card fields visually (same labels, layout, placeholders and input styling),
// but card number, expiry and CVC are Stripe's split elements: card data stays inside Stripe's
// iframes and never touches our code (PCI SAQ A). Only the cardholder name is our own input.
const stripeOptions = (disabled?: boolean) => ({
  disabled,
  style: {
    base: {
      color: stripeColor.foreground,
      fontFamily,
      fontSize: '14px',
      lineHeight: '20px',
      '::placeholder': { color: stripeColor.mutedForeground }
    },
    invalid: { color: stripeColor.destructive }
  }
})

export const CellularCardForm: FC<CellularCardFormProps> = ({ value, disabled, onChange }) => {
  const classes = useFormStyles()
  const options = stripeOptions(disabled)
  const [errors, setErrors] = useState<Record<CardPart, string>>({ number: '', expiry: '', cvc: '' })
  const stripeClass = `${classes.input} ${classes.stripeInput}`

  const track = (part: CardPart) => (event: CardFieldChange) => {
    setErrors(previous => ({ ...previous, [part]: (event.error && event.error.message) || '' }))
    onChange(previous => ({ ...previous, complete: { ...previous.complete, [part]: event.complete } }))
  }

  const fieldError = (part: CardPart) =>
    errors[part] ? <p className={classes.fieldError}>{errors[part]}</p> : null

  return (
    <div className={classes.fields}>
      <div className={`${classes.field} ${classes.wide}`}>
        <label
          className={classes.label}
          htmlFor='cellular-card-name'
        >
          Name on card
        </label>
        <input
          autoComplete='cc-name'
          className={classes.input}
          disabled={disabled}
          id='cellular-card-name'
          maxLength={100}
          onChange={event => {
            const name = event.target.value
            onChange(previous => ({ ...previous, name }))
          }}
          placeholder='Jane Doe'
          value={value.name}
        />
      </div>
      <div className={`${classes.field} ${classes.wide}`}>
        <label
          className={classes.label}
          htmlFor='cellular-card-number'
        >
          Card number
        </label>
        <CardNumberElement
          className={stripeClass}
          id='cellular-card-number'
          onChange={track('number')}
          options={{ ...options, placeholder: '1234 5678 9012 3456' }}
        />
        {fieldError('number')}
      </div>
      <div className={classes.field}>
        <label
          className={classes.label}
          htmlFor='cellular-card-expiry'
        >
          Expiry (MM/YY)
        </label>
        <CardExpiryElement
          className={stripeClass}
          id='cellular-card-expiry'
          onChange={track('expiry')}
          options={{ ...options, placeholder: 'MM/YY' }}
        />
        {fieldError('expiry')}
      </div>
      <div className={classes.field}>
        <label
          className={classes.label}
          htmlFor='cellular-card-cvc'
        >
          CVC
        </label>
        <CardCvcElement
          className={stripeClass}
          id='cellular-card-cvc'
          onChange={track('cvc')}
          options={{ ...options, placeholder: '123' }}
        />
        {fieldError('cvc')}
      </div>
    </div>
  )
}
